import type { UserInfo, UserFeatures } from '@/types';

const REQUIRED_FIELDS = [
  'id',
  'name',
  'email',
  'standAloneToken',
  'serrea',
  'alertsURI',
  'adaURI',
  'activeboardsURI',
  'devoDomain',
];

// Derives a UserInfo object from the metadata passed in via postMessage API.
// Features are intentionally NOT restored here - they will be fetched fresh from server
export const makeUserInfoFromMetadata = (metadata: any) =>
  metadata?.credential && metadata?.userDomain?.userId
    ? {
        id: metadata.userDomain.userId,
        name: metadata.userDomain.userName,
        email: metadata.userDomain.email,
        standAloneToken: metadata.credential.standAloneToken,
        serrea: metadata.credential.serrea,
        adaURI: metadata.credential.adaURI,
        alertsURI: metadata.credential.alertsURI,
        activeboardsURI: metadata.credential.activeboardsURI,
        theme: metadata.userWebSettings?.theme,
        devoDomain: metadata.domainInfo?.name,
        // Features are intentionally NOT restored - will be fetched fresh via useUserDetailsWithFeatures
      }
    : null;

// Derives a UserInfo object from the login API response.
export const makeUserInfoFromLoginResponse = (
  loginResponse: any,
  features?: UserFeatures
): UserInfo | null => {
  if (!loginResponse?.credential || !loginResponse?.userDomain || !loginResponse?.domainInfo) {
    console.warn('makeUserInfoFromLoginResponse: Missing required fields in login response');
    return null;
  }
  return {
    id: String(loginResponse.userDomain.id || ''),
    name: loginResponse.userDomain.userName || loginResponse.userDomain.email || '',
    email: loginResponse.userDomain.email || '',
    standAloneToken: loginResponse.credential.standAloneToken || '',
    serrea: loginResponse.credential.serrea || '',
    adaURI: loginResponse.credential.adaURI || '',
    alertsURI: loginResponse.credential.alertsURI || '',
    activeboardsURI: loginResponse.credential.activeboardsURI || '',
    devoDomain: loginResponse.domainInfo.name || '',
    theme: loginResponse.userWebSettings?.theme,
    jwt: loginResponse.jwt || '',
    features: features || undefined,
  };
};

// Derives a UserInfo object from Keycloak credentials stored in localStorage
// Features are intentionally NOT restored here - they will be fetched fresh from server
export const makeUserInfoFromKeycloakMetadata = (keycloakData: any): UserInfo | null => {
  if (!keycloakData?.access_token || !keycloakData?.user_info) {
    return null;
  }

  const userInfo = keycloakData.user_info;

  return {
    id: userInfo.sub || '',
    name: userInfo.name || userInfo.preferred_username || userInfo.email || '',
    email: userInfo.email || '',
    standAloneToken: keycloakData.access_token,
    serrea: '', // Not available in Keycloak
    adaURI: '', // Not available in Keycloak
    alertsURI: '', // Not available in Keycloak
    activeboardsURI: '', // Not available in Keycloak
    devoDomain: keycloakData.realm || 'matrix',
    jwt: keycloakData.jwt || keycloakData.access_token,
    // Features are intentionally NOT restored - will be fetched fresh via useUserDetailsWithFeatures
  };
};

// Returns the first required field which is missing/empty in the given userInfo object.
// Returns '*' if the userInfo is null/undefined. Returns null if the userInfo is valid.
export const getUserInfoInvalidField = (userInfo: UserInfo | null | undefined) => {
  if (!userInfo) {
    return '*';
  }
  return REQUIRED_FIELDS.find(key => !String(userInfo[key as keyof UserInfo] ?? '')) || null;
};
